import type { $Fetch } from 'ofetch';
import type {
  SolicitacaoConclusaoGateway, ListSolicitacoesConclusaoParams,
  FilaSolicitacaoConclusaoParams,
} from '@/application/port/out/SolicitacaoConclusaoGateway';
import type {
  SolicitacaoConclusao, SolicitacaoConclusaoEtapa,
  SolicitacaoConclusaoDocumento, SolicitacaoConclusaoHistorico,
  DecidirEtapaCommand, PedirComplementacaoCommand,
  RegistrarAcademicoCommand, ReverterCommand,
} from '@/domain/model/SolicitacaoConclusao';
import type { Page } from '@/domain/model/Page';
import type { ApiResponse } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';

const PATH = '/api/v1/solicitacoes-conclusao';
const ADMIN_PATH = '/api/v1/admin/solicitacoes-conclusao';

/**
 * HTTP gateway leitura admin de SolicitacaoConclusao.
 * Authority: acadadmin:solicitacao_conclusao:read (todas as queries).
 *
 * @since G.5.11
 */
export class HttpSolicitacaoConclusaoGateway implements SolicitacaoConclusaoGateway {
  constructor(private readonly client: $Fetch) {}

  async list(params: ListSolicitacoesConclusaoParams): Promise<Page<SolicitacaoConclusao>> {
    const query: Record<string, string | number> = {
      page: params.page ?? 0,
      size: params.size ?? 50,
    };
    if (params.status) query.status = params.status;
    if (params.courseId) query.courseId = params.courseId;
    const res = await this.client<ApiResponse<Page<SolicitacaoConclusao>>>(
      PATH, { method: 'GET', params: query });
    assertApiSuccess(res);
    return res.data;
  }

  async get(id: string): Promise<SolicitacaoConclusao> {
    const res = await this.client<ApiResponse<SolicitacaoConclusao>>(
      `${PATH}/${encodeURIComponent(id)}`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }

  async listEtapas(id: string): Promise<SolicitacaoConclusaoEtapa[]> {
    const res = await this.client<ApiResponse<SolicitacaoConclusaoEtapa[]>>(
      `${PATH}/${encodeURIComponent(id)}/etapas`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }

  async listDocumentos(id: string): Promise<SolicitacaoConclusaoDocumento[]> {
    const res = await this.client<ApiResponse<SolicitacaoConclusaoDocumento[]>>(
      `${PATH}/${encodeURIComponent(id)}/documentos`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }

  async listHistorico(id: string): Promise<SolicitacaoConclusaoHistorico[]> {
    const res = await this.client<ApiResponse<SolicitacaoConclusaoHistorico[]>>(
      `${PATH}/${encodeURIComponent(id)}/historico`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }

  /* ─── Workflow G.5.12 ─── */

  async fila(params: FilaSolicitacaoConclusaoParams): Promise<Page<SolicitacaoConclusaoEtapa>> {
    const query: Record<string, string | number> = {
      page: params.page ?? 0,
      size: params.size ?? 50,
    };
    const res = await this.client<ApiResponse<Page<SolicitacaoConclusaoEtapa>>>(
      `${ADMIN_PATH}/fila/${encodeURIComponent(params.papel)}`,
      { method: 'GET', params: query });
    assertApiSuccess(res);
    return res.data;
  }

  async claimEtapa(etapaId: string): Promise<SolicitacaoConclusaoEtapa> {
    const res = await this.client<ApiResponse<SolicitacaoConclusaoEtapa>>(
      `${ADMIN_PATH}/etapas/${encodeURIComponent(etapaId)}/claim`,
      { method: 'POST' });
    assertApiSuccess(res);
    return res.data;
  }

  async decidirEtapa(etapaId: string, cmd: DecidirEtapaCommand): Promise<SolicitacaoConclusao> {
    const res = await this.client<ApiResponse<SolicitacaoConclusao>>(
      `${ADMIN_PATH}/etapas/${encodeURIComponent(etapaId)}/decidir`,
      { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }

  async pedirComplementacao(id: string, cmd: PedirComplementacaoCommand): Promise<SolicitacaoConclusao> {
    const res = await this.client<ApiResponse<SolicitacaoConclusao>>(
      `${ADMIN_PATH}/${encodeURIComponent(id)}/complementacao`,
      { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }

  async registrarAcademico(etapaId: string, cmd: RegistrarAcademicoCommand): Promise<SolicitacaoConclusao> {
    const res = await this.client<ApiResponse<SolicitacaoConclusao>>(
      `${ADMIN_PATH}/etapas/${encodeURIComponent(etapaId)}/registrar`,
      { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }

  async reenviar(id: string): Promise<SolicitacaoConclusao> {
    const res = await this.client<ApiResponse<SolicitacaoConclusao>>(
      `${ADMIN_PATH}/${encodeURIComponent(id)}/reenviar`,
      { method: 'POST' });
    assertApiSuccess(res);
    return res.data;
  }

  async reverter(id: string, cmd: ReverterCommand): Promise<SolicitacaoConclusao> {
    const res = await this.client<ApiResponse<SolicitacaoConclusao>>(
      `${ADMIN_PATH}/${encodeURIComponent(id)}/reverter`,
      { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }
}
